/**
 * Image-build readiness API service (NC-967 UI half).
 *
 * Wraps `GET /api/images-build-readiness?workloadId=` (BE merged in #992). The
 * backend applies the SAME installer-readiness logic as the images-build
 * dispatch gate, so this lets `BuildImageDialog` show a readiness Ampel + the
 * concrete per-app gaps BEFORE a tutor starts a 60–90 min / €5–10 build (and
 * before the BE would reject the dispatch with a 400).
 *
 * Also the reusable readiness step for the #972 wizard.
 */

import apiClient from './api';
import { ApiResponse } from './types';

/** Per-application installer readiness (mirrors api/shared/installerReadiness.ts). */
export interface AppReadiness {
  applicationId: string;
  displayName: string;
  ready: boolean;
  /** Populated only when ready === false. */
  reason?: string;
}

/** Aggregate installer readiness for a workload. */
export interface WorkloadReadiness {
  workloadId: string;
  ready: boolean;
  apps: AppReadiness[];
}

export const imageReadinessApi = {
  /**
   * GET /api/images-build-readiness?workloadId=
   * Operator+; tenant-gated server-side. Returns the per-app installer
   * readiness so the caller can gate/annotate the build UI.
   */
  async get(workloadId: string): Promise<WorkloadReadiness> {
    const response = await apiClient.get<ApiResponse<WorkloadReadiness>>(
      '/images-build-readiness',
      { params: { workloadId } }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch build readiness');
    }
    return response.data.data;
  },
};
